import { formatDateNumber } from '@seen/core/dates'
import { featureFlags } from '@seen/core/featureFlags'
import type { ExtendedImmunization } from '@seen/core/models/immunizations/ExtendedImmunization'
import { Badge } from '@seen/ui'
import type { ColumnDef } from '@tanstack/react-table'
import { type JSX, useMemo } from 'react'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { extractTextFromContent } from '#src/utils/noteContent.ts'

interface ImmunizationListViewProps {
  immunizations: ExtendedImmunization[]
  onRowClick: (immunization: ExtendedImmunization) => void
}

export function ImmunizationListView({
  immunizations,
  onRowClick,
}: ImmunizationListViewProps): JSX.Element {
  const showNotes = useFeatureFlag(featureFlags.immunizationListNotes)
  const columns = useMemo<ColumnDef<ExtendedImmunization>[]>(
    () => [
      {
        id: 'vaccine',
        header: 'Vaccine',
        accessorFn: (immunization) => immunization.vaccineDisplay,
        cell: ({ row }) => {
          const isRefused = row.original.isNotDone && row.original.refusalReason
          const note = row.original.notes
            ? extractTextFromContent(row.original.notes)
            : ''
          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 font-medium">
                {row.original.vaccineDisplay}
                {isRefused ? (
                  <Badge variant="secondary" className="font-medium">
                    Declined
                  </Badge>
                ) : null}
              </div>
              {isRefused && (
                <div className="text-sm text-muted-foreground">
                  {row.original.refusalReason}
                </div>
              )}
              {showNotes && note && (
                <div className="text-sm text-muted-foreground">
                  Notes: {note}
                </div>
              )}
            </div>
          )
        },
      },
      {
        id: 'administeredDate',
        header: 'Administered',
        accessorFn: (immunization) => immunization.administeredDate,
        cell: ({ row }) =>
          row.original.administeredDate ? (
            <span>{formatDateNumber(row.original.administeredDate)}</span>
          ) : null,
      },
    ],
    [showNotes]
  )

  return (
    <DataTable columns={columns} data={immunizations} onRowClick={onRowClick} />
  )
}
